import type { GraphResponse } from '@axilon-apps/domains-ontology-ui-state'
import {
  ANCHOR_NODE_LABELS,
  SUBANCHOR_NODE_LABELS,
  CLUSTER_ANCHOR_LABELS,
  CROSS_DOMAIN_RELATIONSHIP_TYPES,
  DEFAULT_NODE_COLOR,
  EDGE_COLOR_INTRA,
  EDGE_COLOR_CROSS,
  colorForNodeLabel
} from '@axilon-apps/domains-ontology-ui-state'

/**
 * Maps ontology graph data to the flat `Float32Array` buffers that
 * [cosmos.gl](https://cosmos.gl) (`@cosmos.gl/graph`) consumes. cosmos.gl is a
 * GPU-accelerated force layout: every attribute (positions, colors, sizes,
 * links, link colors/widths) is a typed array keyed by point/link index, so
 * this module owns the ontology-`_id` ⇄ point-index mapping that the rest of
 * the canvas relies on.
 *
 * Like the previous NVL renderer there is no "layer" concept after the SCADA
 * ontology restructure — every node is a peer. We keep a minimal visual
 * grouping by sizing anchors (Organization/Site/Compute) and sub-anchors
 * (TagProvider/Project) larger, and tinting cross-domain edges differently.
 *
 * Selection / hover / highlight emphasis is computed separately (see
 * `buildPointVisuals` / `buildLinkVisuals`) and pushed via the cheap
 * `setPointColors` / `setLinkColors` GPU paths so we never rebuild structural
 * buffers (positions/links) on a hover tick.
 */

const PRIMARY_ANCHOR_SIZE = 35
const SUBANCHOR_SIZE = 20
const DEFAULT_SIZE = 5

/** Emphasis bump (px) added to a point's base size when selected or hovered. */
const EMPHASIS_SIZE_BONUS = 5

const INTRA_LINK_WIDTH = 1
const CROSS_LINK_WIDTH = 1.6

export type Rgba = [number, number, number, number]

/**
 * Converts a hex color (`#rrggbb` / `#rgb`) to cosmos.gl's normalized RGBA
 * (each channel 0–1). cosmos.gl stores point/link colors directly in float
 * textures, so callers of `setPointColors` / `setLinkColors` must pre-normalize
 * exactly like the library's internal `getRgbaColor` does for default colors.
 */
export function hexToRgba(hex: string, alpha = 1): Rgba {
  let value = hex.trim().replace(/^#/, '')
  if (value.length === 3) {
    value = value
      .split('')
      .map((c) => c + c)
      .join('')
  }
  const int = Number.parseInt(value, 16)
  const r = (int >> 16) & 255
  const g = (int >> 8) & 255
  const b = int & 255
  return [r / 255, g / 255, b / 255, alpha]
}

/** State emphasis palette (mirrors the old NVL selection/hover styling). */
export const POINT_COLOR_BASE = hexToRgba(DEFAULT_NODE_COLOR)
export const POINT_COLOR_SELECTED = hexToRgba('#0ea5e9')
export const POINT_COLOR_HIGHLIGHT = hexToRgba('#38bdf8')
export const POINT_COLOR_NEIGHBOR = hexToRgba('#7dd3fc')
export const POINT_COLOR_HOVER = hexToRgba('#38bdf8')
export const POINT_COLOR_DIM = hexToRgba('#334155', 0.45)

const LINK_COLOR_INTRA = hexToRgba(EDGE_COLOR_INTRA, 0.55)
const LINK_COLOR_CROSS = hexToRgba(EDGE_COLOR_CROSS, 0.7)
const LINK_COLOR_SELECTED = hexToRgba('#38bdf8', 0.95)
const LINK_COLOR_HOVER = hexToRgba('#7dd3fc', 0.9)

export interface CosmosGraphData {
  /** Number of points (nodes). */
  pointCount: number
  /** Number of links (relationships, filtered to those with both endpoints present). */
  linkCount: number
  /** `[source0, target0, source1, target1, …]` point indices. */
  links: Float32Array
  /** Per-point base size (no emphasis applied). */
  baseSizes: Float32Array
  /** Per-point base RGBA (per node-type color), `[r0,g0,b0,a0, r1,…]`. */
  baseColors: Float32Array
  /** ontology `_id` → point index. */
  idToIndex: Map<string, number>
  /** point index → ontology `_id`. */
  indexToId: string[]
  /** Per-link source/target indices for neighbor lookups. */
  linkPairs: Array<[number, number]>
  /** Per-link relationship type (for hovered/selected edge captions if needed). */
  linkTypes: string[]
  /** Whether each link is cross-domain (affects base color/width). */
  linkIsCross: boolean[]
  /** index → set of adjacent point indices. */
  adjacency: Map<number, Set<number>>
  /**
   * Per-point cluster index for cosmos.gl's clustering force (`undefined` =
   * unclustered). Each structural container (see `CLUSTER_ANCHOR_LABELS`) is its
   * own cluster; nodes join their nearest container so containers nest.
   */
  pointClusters: Array<number | undefined>
  /** Number of distinct clusters (max cluster index + 1), for sanity/debug. */
  clusterCount: number
  /** Per-cluster container display name (indexed by cluster id). */
  clusterLabels: string[]
  /** Per-cluster container node label, e.g. "Controller" (indexed by cluster id). */
  clusterTypes: string[]
}

function pointSizeForLabel(label: unknown): number {
  if (ANCHOR_NODE_LABELS.has(label as never)) return PRIMARY_ANCHOR_SIZE
  if (SUBANCHOR_NODE_LABELS.has(label as never)) return SUBANCHOR_SIZE
  return DEFAULT_SIZE
}

/**
 * Assigns each point to a structural cluster via multi-source BFS from every
 * "anchor" (container) node — see `CLUSTER_ANCHOR_LABELS`.
 *
 * Every anchor instance seeds its own cluster at distance 0; all other nodes
 * join whichever anchor is fewest hops away (ties broken deterministically by
 * lowest anchor index). Because distance is by hops, nested containers behave
 * intuitively: a UDT definition's members sit one hop from the definition and
 * many hops from the surrounding tag provider, so they cluster with the UDT.
 * Nodes with no path to any anchor stay unclustered (`undefined`).
 */
function buildPointClusters(
  labels: string[],
  adjacency: Map<number, Set<number>>,
  pointCount: number
): {
  clusters: Array<number | undefined>
  clusterCount: number
  anchorIndexByCluster: number[]
} {
  const clusters: Array<number | undefined> = new Array(pointCount).fill(undefined)
  const anchorIndexByCluster: number[] = []

  // Seed one cluster per anchor node (in index order for deterministic ties).
  const queue: number[] = []
  let clusterCount = 0
  for (let i = 0; i < pointCount; i++) {
    if (CLUSTER_ANCHOR_LABELS.has(labels[i] as never)) {
      clusters[i] = clusterCount
      anchorIndexByCluster[clusterCount] = i
      clusterCount++
      queue.push(i)
    }
  }

  // Standard multi-source BFS: FIFO over the undirected adjacency gives every
  // node its nearest anchor's cluster.
  for (let head = 0; head < queue.length; head++) {
    const current = queue[head]
    const cluster = clusters[current]
    const neighbors = adjacency.get(current)
    if (!neighbors) continue
    for (const neighbor of neighbors) {
      if (clusters[neighbor] === undefined) {
        clusters[neighbor] = cluster
        queue.push(neighbor)
      }
    }
  }

  return { clusters, clusterCount, anchorIndexByCluster }
}

/** Best available human-readable name for a node (displayName → name → id → _id). */
function nodeDisplayName(node: GraphResponse['nodes'][number]): string {
  const candidates = [node.displayName, node.name, node.id]
  for (const candidate of candidates) {
    if (typeof candidate === 'string' && candidate.trim() !== '') return candidate
  }
  return node._id
}

/** Builds the structural cosmos.gl buffers (everything except positions/colors). */
export function toCosmosElements(graph: GraphResponse): CosmosGraphData {
  const pointCount = graph.nodes.length
  const idToIndex = new Map<string, number>()
  const indexToId: string[] = new Array(pointCount)
  const labels: string[] = new Array(pointCount)
  const baseSizes = new Float32Array(pointCount)
  const baseColors = new Float32Array(pointCount * 4)

  graph.nodes.forEach((node, i) => {
    idToIndex.set(node._id, i)
    indexToId[i] = node._id
    labels[i] = node.label
    baseSizes[i] = pointSizeForLabel(node.label)
    const [r, g, b, a] = hexToRgba(colorForNodeLabel(node.label))
    baseColors[i * 4] = r
    baseColors[i * 4 + 1] = g
    baseColors[i * 4 + 2] = b
    baseColors[i * 4 + 3] = a
  })

  const linkPairs: Array<[number, number]> = []
  const linkTypes: string[] = []
  const linkIsCross: boolean[] = []
  const adjacency = new Map<number, Set<number>>()

  for (const rel of graph.relationships) {
    const source = idToIndex.get(rel.sourceId)
    const target = idToIndex.get(rel.targetId)
    if (source === undefined || target === undefined) continue

    linkPairs.push([source, target])
    linkTypes.push(rel.type)
    linkIsCross.push(CROSS_DOMAIN_RELATIONSHIP_TYPES.has(rel.type))

    let sourceAdj = adjacency.get(source)
    if (!sourceAdj) {
      sourceAdj = new Set<number>()
      adjacency.set(source, sourceAdj)
    }
    sourceAdj.add(target)

    let targetAdj = adjacency.get(target)
    if (!targetAdj) {
      targetAdj = new Set<number>()
      adjacency.set(target, targetAdj)
    }
    targetAdj.add(source)
  }

  const linkCount = linkPairs.length
  const links = new Float32Array(linkCount * 2)
  for (let i = 0; i < linkCount; i++) {
    links[i * 2] = linkPairs[i][0]
    links[i * 2 + 1] = linkPairs[i][1]
  }

  const {
    clusters: pointClusters,
    clusterCount,
    anchorIndexByCluster
  } = buildPointClusters(labels, adjacency, pointCount)

  const clusterLabels: string[] = new Array(clusterCount)
  const clusterTypes: string[] = new Array(clusterCount)
  for (let c = 0; c < clusterCount; c++) {
    const anchorIndex = anchorIndexByCluster[c]
    clusterLabels[c] = nodeDisplayName(graph.nodes[anchorIndex])
    clusterTypes[c] = labels[anchorIndex]
  }

  return {
    pointCount,
    linkCount,
    links,
    baseSizes,
    baseColors,
    pointClusters,
    clusterCount,
    clusterLabels,
    clusterTypes,
    idToIndex,
    indexToId,
    linkPairs,
    linkTypes,
    linkIsCross,
    adjacency
  }
}

/**
 * Builds an initial `[x0, y0, x1, y1, …]` position buffer. Nodes that already
 * have a known position (from a previous render) keep it so incremental
 * neighbor expansion in explorer mode stays stable; new nodes are seeded near
 * a connected, already-placed neighbor when possible, otherwise scattered
 * randomly around the center of the space.
 */
export function buildPointPositions(
  data: CosmosGraphData,
  knownPositions: Map<string, [number, number]>,
  spaceSize: number
): Float32Array {
  const positions = new Float32Array(data.pointCount * 2)
  const center = spaceSize / 2
  const seededRadius = spaceSize * 0.38

  for (let i = 0; i < data.pointCount; i++) {
    const id = data.indexToId[i]
    const known = knownPositions.get(id)
    if (known) {
      positions[i * 2] = known[0]
      positions[i * 2 + 1] = known[1]
      continue
    }

    // Seed near a connected, already-placed neighbor for nicer fan-outs.
    let seedX: number | undefined
    let seedY: number | undefined
    const neighbors = data.adjacency.get(i)
    if (neighbors) {
      for (const neighborIndex of neighbors) {
        const neighborId = data.indexToId[neighborIndex]
        const neighborPos = knownPositions.get(neighborId)
        if (neighborPos) {
          seedX = neighborPos[0]
          seedY = neighborPos[1]
          break
        }
      }
    }

    const angle = Math.random() * Math.PI * 2
    if (seedX !== undefined && seedY !== undefined) {
      const r = 80 + Math.random() * 160
      positions[i * 2] = seedX + r * Math.cos(angle)
      positions[i * 2 + 1] = seedY + r * Math.sin(angle)
    } else {
      const r = Math.random() * seededRadius
      positions[i * 2] = center + r * Math.cos(angle)
      positions[i * 2 + 1] = center + r * Math.sin(angle)
    }
  }

  return positions
}

function neighborIndicesOf(data: CosmosGraphData, indices: Iterable<number>): Set<number> {
  const result = new Set<number>()
  for (const index of indices) {
    const adj = data.adjacency.get(index)
    if (!adj) continue
    for (const n of adj) result.add(n)
  }
  return result
}

/** Per-point colors + sizes for the current selection / hover / highlight state.
 *
 * `sizeScale` multiplies every node's base size (the emphasis bump stays
 * additive on top), used to enlarge nodes when viewing a filtered cluster.
 */
export function buildPointVisuals(
  data: CosmosGraphData,
  selectedIds: Set<string>,
  highlightedIds: Set<string>,
  hoveredId: string | null,
  sizeScale = 1
): { colors: Float32Array; sizes: Float32Array } {
  const colors = new Float32Array(data.pointCount * 4)
  const sizes = new Float32Array(data.pointCount)

  const selectedIndices = new Set<number>()
  for (const id of selectedIds) {
    const index = data.idToIndex.get(id)
    if (index !== undefined) selectedIndices.add(index)
  }

  const hasSelection = selectedIndices.size > 0
  const hoveredIndex = hoveredId ? data.idToIndex.get(hoveredId) : undefined
  const hasHover = !hasSelection && hoveredIndex !== undefined

  const selectionNeighbors = hasSelection
    ? neighborIndicesOf(data, selectedIndices)
    : new Set<number>()
  for (const index of selectedIndices) selectionNeighbors.delete(index)

  const hoverNeighbors =
    hasHover && hoveredIndex !== undefined
      ? neighborIndicesOf(data, [hoveredIndex])
      : new Set<number>()

  for (let i = 0; i < data.pointCount; i++) {
    const id = data.indexToId[i]
    const isSelected = selectedIndices.has(i)
    const isHighlighted = highlightedIds.has(id)
    const isSelectionNeighbor = selectionNeighbors.has(i)
    const isHovered = i === hoveredIndex
    const isHoverNeighbor = hoverNeighbors.has(i)

    // Default: the node's per-type palette color (read straight from baseColors).
    // Any emphasis state (selection / highlight / hover / dim) overrides it below.
    let color: Rgba | null = null
    let bonus = 0

    if (isSelected) {
      color = POINT_COLOR_SELECTED
      bonus = EMPHASIS_SIZE_BONUS
    } else if (isHighlighted) {
      color = POINT_COLOR_HIGHLIGHT
      bonus = EMPHASIS_SIZE_BONUS
    } else if (hasSelection && isSelectionNeighbor) {
      color = POINT_COLOR_NEIGHBOR
    } else if (hasHover && isHovered) {
      color = POINT_COLOR_HOVER
      bonus = EMPHASIS_SIZE_BONUS
    } else if (hasHover && isHoverNeighbor) {
      color = POINT_COLOR_NEIGHBOR
    }

    if (color) {
      colors[i * 4] = color[0]
      colors[i * 4 + 1] = color[1]
      colors[i * 4 + 2] = color[2]
      colors[i * 4 + 3] = color[3]
    } else {
      colors[i * 4] = data.baseColors[i * 4]
      colors[i * 4 + 1] = data.baseColors[i * 4 + 1]
      colors[i * 4 + 2] = data.baseColors[i * 4 + 2]
      colors[i * 4 + 3] = data.baseColors[i * 4 + 3]
    }
    sizes[i] = data.baseSizes[i] * sizeScale + bonus
  }

  return { colors, sizes }
}

/** Per-link colors + widths for the current selection / hover state. */
export function buildLinkVisuals(
  data: CosmosGraphData,
  selectedIds: Set<string>,
  hoveredId: string | null
): { colors: Float32Array; widths: Float32Array } {
  const colors = new Float32Array(data.linkCount * 4)
  const widths = new Float32Array(data.linkCount)

  const focusIndices = new Set<number>()
  for (const id of selectedIds) {
    const index = data.idToIndex.get(id)
    if (index !== undefined) focusIndices.add(index)
  }
  const hasSelection = focusIndices.size > 0
  if (!hasSelection && hoveredId) {
    const hoveredIndex = data.idToIndex.get(hoveredId)
    if (hoveredIndex !== undefined) focusIndices.add(hoveredIndex)
  }
  const hasFocus = focusIndices.size > 0

  for (let i = 0; i < data.linkCount; i++) {
    const [source, target] = data.linkPairs[i]
    const isCross = data.linkIsCross[i]
    let color = isCross ? LINK_COLOR_CROSS : LINK_COLOR_INTRA
    let width = isCross ? CROSS_LINK_WIDTH : INTRA_LINK_WIDTH

    if (hasFocus) {
      const isConnected = focusIndices.has(source) || focusIndices.has(target)
      if (isConnected) {
        color = hasSelection ? LINK_COLOR_SELECTED : LINK_COLOR_HOVER
        width = hasSelection ? 3 : 2
      }
    }

    colors[i * 4] = color[0]
    colors[i * 4 + 1] = color[1]
    colors[i * 4 + 2] = color[2]
    colors[i * 4 + 3] = color[3]
    widths[i] = width
  }

  return { colors, widths }
}
